import { Button } from '@/components/ui';
import { ArrowLeft, X, Pencil, Trash2 } from 'lucide-react';

interface ThreeColumnEditorProps {
  title: string;
  titleBadge?: React.ReactNode;
  headerMetadata?: React.ReactNode;
  onBack?: () => void;
  onCancel: () => void;
  onSave?: () => void;
  onSaveAndClose?: () => void;
  saving?: boolean;
  saveDisabled?: boolean;
  viewMode?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  leftPanel: React.ReactNode;
  middlePanel: React.ReactNode;
  rightPanel: React.ReactNode;
  leftTitle?: string;
  middleTitle?: string;
  rightTitle?: string;
  leftHeaderAction?: React.ReactNode;
  middleHeaderAction?: React.ReactNode;
  rightHeaderAction?: React.ReactNode;
}

export function ThreeColumnEditor({
  title,
  titleBadge,
  headerMetadata,
  onBack,
  onCancel,
  onSave,
  onSaveAndClose,
  saving = false,
  saveDisabled = false,
  viewMode = false,
  onEdit,
  onDelete,
  deleteDisabled = false,
  leftPanel,
  middlePanel,
  rightPanel,
  leftTitle = 'Details',
  middleTitle = 'Sub-items',
  rightTitle = 'Attachments',
  leftHeaderAction,
  middleHeaderAction,
  rightHeaderAction,
}: ThreeColumnEditorProps) {
  return (
    <div className="md:h-full flex flex-col bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between px-6 py-4 border-b border-gray-200 gap-3 md:gap-0">
        <div className="flex items-center gap-3 flex-wrap">
          {onBack && (
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
          {titleBadge}
          {headerMetadata}
        </div>
        <div className="flex items-center gap-3 flex-wrap md:flex-nowrap">
          {viewMode ? (
            <>
              <Button variant="ghost" size="sm" onClick={onCancel}>
                <X className="h-4 w-4 mr-1" />
                Close
              </Button>
              {onDelete && (
                <Button variant="danger" size="sm" onClick={onDelete} disabled={deleteDisabled}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              )}
              {onEdit && (
                <Button onClick={onEdit}>
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={onCancel} className="text-gray-500 hover:text-gray-700">
                Close
              </Button>
              {onDelete && (
                <Button variant="danger" size="sm" onClick={onDelete} disabled={deleteDisabled}>
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              )}
              {onSave && (
                <Button onClick={onSave} loading={saving} disabled={saveDisabled}>
                  Save
                </Button>
              )}
              {onSaveAndClose && (
                <Button onClick={onSaveAndClose} loading={saving} disabled={saveDisabled}>
                  {onSave ? 'Save & Close' : 'Save'}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* 3-column body - independent tiles */}
      <div className="md:flex-1 grid grid-cols-1 md:grid-cols-3 gap-3 p-3 md:min-h-0">
        {/* Left Panel - Details */}
        <div className="flex flex-col md:min-h-0 bg-gray-50/30 rounded-lg border border-gray-100">
          <div className="h-11 px-4 border-b border-gray-100 bg-gray-50/50 rounded-t-lg flex items-center justify-between flex-shrink-0">
            <h2 className="text-sm font-medium text-gray-600">{leftTitle}</h2>
            {leftHeaderAction}
          </div>
          <div className="md:flex-1 md:overflow-y-auto p-4 scrollbar-hide">
            {leftPanel}
          </div>
        </div>

        {/* Middle Panel */}
        <div className="flex flex-col md:min-h-0 bg-gray-50/30 rounded-lg border border-gray-100">
          <div className="h-11 px-4 border-b border-gray-100 bg-gray-50/50 rounded-t-lg flex items-center justify-between flex-shrink-0">
            <h2 className="text-sm font-medium text-gray-600">{middleTitle}</h2>
            {middleHeaderAction}
          </div>
          <div className="md:flex-1 md:overflow-y-auto p-4 scrollbar-hide">
            {middlePanel}
          </div>
        </div>

        {/* Right Panel */}
        <div className="flex flex-col md:min-h-0 bg-gray-50/30 rounded-lg border border-gray-100">
          <div className="h-11 px-4 border-b border-gray-100 bg-gray-50/50 rounded-t-lg flex items-center justify-between flex-shrink-0">
            <h2 className="text-sm font-medium text-gray-600">{rightTitle}</h2>
            {rightHeaderAction}
          </div>
          <div className="md:flex-1 md:overflow-y-auto p-4 scrollbar-hide">
            {rightPanel}
          </div>
        </div>
      </div>
    </div>
  );
}
